import axios from 'axios';
import { convertDwg } from 'components/ModelViewer/convertDwg';
import {
  isExportOnlyModelFile,
  isModelFile,
  opensInModelViewer,
} from 'components/ModelViewer/modelFormats';
import { modelPreviewStored } from 'scenes/ProjectBank/store/actions/project-bank.actions';
import {
  postConfirmModelPreview,
  postModelPreviewUploadUrl,
} from 'scenes/ProjectBank/services/modelPreview.service';

/**
 * Tile pictures of the bank's 3D model files. The backend draws thumbnails
 * of images and videos only, so the browser draws a model's: when the file
 * is uploaded from this device, and otherwise the first time someone opens
 * it in the viewer. A DWG's is drawn of the server's conversion of it. The API stores the PNG at the file's thumbnail key and
 * records which renderer version drew it on the row
 * (`modelThumbnailVersion`), so a tile asks only for previews that exist.
 * Raising MODEL_PREVIEW_VERSION has every preview drawn again as files are
 * opened.
 */
export const MODEL_PREVIEW_VERSION = 1;

/**
 * Uploads larger than this get their preview on first open instead: building
 * a model holds the page, which is fair when someone asked to see it, not
 * right after an upload. For a DWG the limit is on its converted model.
 */
export const MODEL_PREVIEW_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

/**
 * Whether a raw bank file is a 3D model without a preview from the current
 * renderer.
 * @param {?Object} raw public FilePermission or private bank item
 * @returns {boolean}
 */
export function needsModelPreview(raw) {
  if (!raw || raw.type === 'FOLDER' || !opensInModelViewer(raw.name)) {
    return false;
  }
  const version = raw.modelThumbnailVersion;
  return !(Number.isInteger(version) && version >= MODEL_PREVIEW_VERSION);
}

/**
 * Whether a raw bank file has a model preview to show in its tile.
 * @param {?Object} raw
 * @returns {boolean}
 */
export function hasModelPreview(raw) {
  return (
    !!raw &&
    opensInModelViewer(raw.name) &&
    Number.isInteger(raw.modelThumbnailVersion) &&
    raw.modelThumbnailVersion >= 1
  );
}

/**
 * Which file a preview is for, the way the API addresses it: a private item
 * (one with an `s3Key`, as fetchBankFileBlob tells them apart) by its project
 * and id, a public FilePermission by id.
 * @param {Object} raw
 * @returns {{mode: 'PUBLIC'|'PRIVATE', id: string, projectId?: string}}
 */
export function modelPreviewTargetOf(raw) {
  return raw.s3Key
    ? { mode: 'PRIVATE', id: raw.id, projectId: raw.projectId }
    : { mode: 'PUBLIC', id: raw.id };
}

// One preview at a time: each builds a whole model on the main thread.
let queue = Promise.resolve();

/**
 * Runs `task` after every preview queued before it, whatever became of them.
 * @param {Function} task `() => Promise`
 * @returns {Promise} the task's own result
 */
export function enqueueModelPreview(task) {
  const run = queue.then(task, task);
  queue = run.catch(() => {});
  return run;
}

/**
 * Uploads a preview PNG for a file and tells the bank's state, so its tile
 * shows it. A preview someone else stored first (409) is no failure.
 * @param {Object} params
 * @param {Function} params.dispatch
 * @param {{mode: string, id: string, projectId?: string}} params.target
 * @param {Blob} params.blob
 * @returns {Promise<boolean>} whether this preview was stored
 */
export async function storeModelPreview({ dispatch, target, blob }) {
  try {
    const { uploadUrl } = await postModelPreviewUploadUrl(
      target,
      MODEL_PREVIEW_VERSION
    );
    await axios.put(uploadUrl, blob, {
      headers: { 'Content-Type': 'image/png' },
    });
    const stored = await postConfirmModelPreview(target, MODEL_PREVIEW_VERSION);
    dispatch(
      modelPreviewStored({
        mode: target.mode,
        id: target.id,
        modelThumbnailAt: stored.modelThumbnailAt,
        modelThumbnailVersion: stored.modelThumbnailVersion,
      })
    );
    return true;
  } catch (error) {
    if (error?.response?.status !== 409) {
      console.warn('Model preview was not stored', error);
    }
    return false;
  }
}

/**
 * The viewer's onPreview for a bank file, or undefined when the file needs
 * no preview drawn (ModelViewerDialog draws one only when given this).
 * @param {Function} dispatch
 * @param {?Object} raw the file the viewer opens
 * @returns {Function|undefined} `(pngBlob) => Promise<boolean>`
 */
export function modelPreviewHandlerFor(dispatch, raw) {
  if (!needsModelPreview(raw)) return undefined;
  const target = modelPreviewTargetOf(raw);
  return (blob) => storeModelPreview({ dispatch, target, blob });
}

// The raw-row shape convertDwg addresses a bank file by: dwgConversionApiPath
// tells a private item by whether it has an `s3Key`, not by its value.
const dwgConversionFileOf = (target, name) =>
  target.mode === 'PRIVATE'
    ? { id: target.id, projectId: target.projectId, name, s3Key: true }
    : { id: target.id, name };

/**
 * A DWG just added to the bank: the server began converting it then, so
 * this mostly waits for that, outside the queue (which is for building
 * models), then draws the converted model in it. A failed conversion
 * (no 3D content and the like) leaves the tile as it is.
 */
async function previewUploadedDwg({ dispatch, target, name }) {
  try {
    const converted = await convertDwg(dwgConversionFileOf(target, name));
    if (converted.status !== 'done' || !converted.glbUrl) return false;
    return await enqueueModelPreview(async () => {
      const { createModelPreviewFromGlbUrl } = await import(
        'components/ModelViewer/modelPreviewFromFile'
      );
      const blob = await createModelPreviewFromGlbUrl(
        converted.glbUrl,
        MODEL_PREVIEW_UPLOAD_MAX_BYTES
      );
      if (!blob) return false;
      return storeModelPreview({ dispatch, target, blob });
    });
  } catch (error) {
    console.warn('DWG preview could not be drawn', error);
    return false;
  }
}

/**
 * Draws and stores the preview of a 3D model file just uploaded from this
 * device, in the background and one at a time. Files too large for that
 * (MODEL_PREVIEW_UPLOAD_MAX_BYTES), and anything that is no model, are
 * left for the viewer. A DWG is drawn from the server's conversion, so call
 * this only once its bank row exists.
 * @param {Object} params
 * @param {Function} params.dispatch
 * @param {{mode: string, id: string, projectId?: string}} params.target
 * @param {File} params.file
 * @returns {Promise<boolean>} whether a preview was stored
 */
export function previewUploadedModelFile({ dispatch, target, file }) {
  if (!file || typeof file !== 'object' || !target || !target.id) {
    return Promise.resolve(false);
  }
  if (isExportOnlyModelFile(file.name)) {
    return previewUploadedDwg({ dispatch, target, name: file.name });
  }
  if (
    !isModelFile(file.name) ||
    !(file.size > 0) ||
    file.size > MODEL_PREVIEW_UPLOAD_MAX_BYTES
  ) {
    return Promise.resolve(false);
  }
  return enqueueModelPreview(async () => {
    // three.js and the loaders stay in the viewer's own chunk.
    const { createModelPreviewFromFile } = await import(
      'components/ModelViewer/modelPreviewFromFile'
    );
    const blob = await createModelPreviewFromFile(file);
    return storeModelPreview({ dispatch, target, blob });
  }).catch((error) => {
    console.warn('Model preview could not be drawn', error);
    return false;
  });
}
